import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { menuTypeService } from '../../../../services/menuTypeService';
import { MenuCategoryDTO, MenuItemTypeDTO } from '../../../../types';
import { CirclePlus, Eye, RefreshCw, SquarePen, LayoutGrid, CheckCircle2 } from 'lucide-react';
import '../RestaurantPage.css';

const MenuCategoryListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<MenuCategoryDTO[]>([]);
  const [types, setTypes] = useState<MenuItemTypeDTO[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTypes();
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTypes = async () => {
    try {
      const res = await menuTypeService.getAll();
      const d = (res.data as any)?.data;
      setTypes(Array.isArray(d) ? d : (d?.data ?? []));
    } catch {
      // non-fatal
    }
  };

  const loadItems = async (typeId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await menuCategoryService.getByType(typeId);
      const raw = (res.data as any)?.data ?? (res.data as any) ?? [];
      const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setItems(data);
    } catch {
      setError(t('restaurant.menuCategory.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await menuCategoryService.getByType(selectedTypeId);
      const raw = (res.data as any)?.data ?? (res.data as any) ?? [];
      const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setItems(data);
    } catch {
      setError(t('restaurant.menuCategory.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleTypeFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value ? parseInt(e.target.value) : undefined;
    setSelectedTypeId(val);
    loadItems(val);
  };

  const activeCount = items.filter((i) => i.isActive).length;

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('restaurant.menuCategory.name'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'typeName',
      label: t('restaurant.menuCategory.type'),
      width: '160px',
      render: (value) =>
        value ? (
          <span className="ims-badge rest-badge-type">{value}</span>
        ) : (
          <span>—</span>
        ),
    },
    {
      key: 'sortOrder',
      label: t('restaurant.menuType.sortOrder'),
      sortable: true,
      width: '100px',
      render: (value) => <span>{value}</span>,
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '120px',
      render: (value) => (
        <span className={`ims-badge ${value ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('common.createdAt'),
      sortable: true,
      width: '140px',
      hideMobile: true,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '—'),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/restaurant/menu-categories/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/restaurant/menu-categories/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
  ];

  return (
    <Layout>
      <div className="ims-page rest-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('restaurant.menuCategory.title')}</h1>
            <p className="ims-list-subtitle">{t('common.manageAndOrganize', 'Manage and organise your menu categories')}</p>
          </div>
          <div className="ims-list-actions">
            <select
              className="ims-form-control"
              value={selectedTypeId ?? ''}
              onChange={handleTypeFilter}
              style={{ minWidth: '150px' }}
            >
              <option value="">{t('restaurant.menuCategory.allTypes')}</option>
              {types.map((tp) => (
                <option key={tp.id} value={tp.id}>
                  {tp.name}
                </option>
              ))}
            </select>
            <button className="btn-refresh" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/restaurant/menu-categories/create')}>
              <CirclePlus size={16} strokeWidth={1.75} />
              {t('restaurant.menuCategory.newCategory')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-orange">
              <LayoutGrid size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">Total Categories</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-green">
              <CheckCircle2 size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">Active Categories</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid
            data={items}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('restaurant.menuCategory.noCategoriesFound')}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>
      </div>
    </Layout>
  );
};

export default MenuCategoryListPage;
